// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

// Quiz Question Types
export enum QuestionDifficulty {
  EASY = 'easy',
  MEDIUM = 'medium',
  HARD = 'hard'
}

export enum QuestionCategory {
  GENERAL_KNOWLEDGE = 'general_knowledge',
  SPORTS = 'sports',
  TECHNOLOGY = 'technology',
  SCIENCE = 'science',
  HISTORY = 'history',
  GEOGRAPHY = 'geography',
  ENTERTAINMENT = 'entertainment',
  LITERATURE = 'literature',
  MATHEMATICS = 'mathematics',
  POLITICS = 'politics'
}

export enum QuestionType {
  MULTIPLE_CHOICE = 'multiple_choice',
  TRUE_FALSE = 'true_false',
  FILL_IN_BLANK = 'fill_in_blank'
}

export enum QuestionStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  PENDING_REVIEW = 'pending_review',
  REJECTED = 'rejected'
}

export interface QuestionOption {
  id: string;
  text: string;
  isCorrect: boolean;
  explanation?: string;
}

export interface Question {
  id: string;
  question: string;
  type: QuestionType;
  category: QuestionCategory;
  difficulty: QuestionDifficulty;
  status: QuestionStatus;
  options: QuestionOption[];
  correctAnswer: string; // For true/false and fill-in-blank
  explanation?: string;
  points: number;
  timeLimit: number; // in seconds
  tags: string[];
  language: string;
  imageUrl?: string;
  audioUrl?: string;
  videoUrl?: string;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
  lastUsed?: string;
  usageCount: number;
  correctAnswerRate: number; // percentage
  averageResponseTime: number; // in seconds
}

// Category Management
export interface QuestionCategoryInfo {
  category: QuestionCategory;
  name: string;
  description: string;
  icon: string;
  color: string;
  totalQuestions: number;
  activeQuestions: number;
  averageDifficulty: number; // 1-3 scale
  popularityScore: number;
  lastUpdated: string;
}

export interface CreateQuestionCategoryRequest {
  category: QuestionCategory;
  name: string;
  description: string;
  icon: string;
  color: string;
}

export interface UpdateQuestionCategoryRequest extends Partial<CreateQuestionCategoryRequest> {
  category: QuestionCategory;
}

export interface DeleteQuestionCategoryRequest {
  category: QuestionCategory;
}

export interface RemoveQuizCategoryRequest {
  category_id: string;
}

// Add quiz question to category (RPC payload)
export interface AddQuizQuestionToCategoryRequest {
  hint?: {
    hintText?: string;
  };
  option1: string;
  option2: string;
  option3: string;
  option4: string;
  isActive: boolean;
  categoryId: string; // e.g., "science_easy_1"
  difficulty: number; // 0=Easy, 1=Medium, 2=Hard
  questionId: string; // unique question identifier
  questionText: string;
  questionTimerSec: number;
  correctOptionIndex: number; // User-facing 1-based index for correct option
}

export interface RemoveQuizQuestionFromCategoryRequest {
  category_id: string;
  question_id: string;
}

export interface GetQuizQuestionsByCategoryRequest {
  category_id: string;
}

// Response type for quiz questions by category (flexible structure)
// Backend returns: { Questions: { "q_0": QuestionData, "q_1": QuestionData, ... } }
export interface QuizQuestionByCategoryResponse {
  Questions?: Record<string, any>; // Backend format: { questionId: QuestionData }
  questions?: any[]; // Array of question objects (alternative format)
  [key: string]: any; // Allow for flexible response structure
}

export interface AddQuizQuestionToCategoryRequest {
  hint?: {
    hintText?: string;
  };
  option1: string;
  option2: string;
  option3: string;
  option4: string;
  isActive: boolean;
  categoryId: string; // e.g., "science_easy_1"
  difficulty: number; // 0=Easy, 1=Medium, 2=Hard
  questionId: string; // unique question identifier
  questionText: string;
  questionTimerSec: number;
  correctOptionIndex: number; // User-facing 1-based index for correct option
}

export interface RemoveQuizQuestionFromCategoryRequest {
  category_id: string;
  question_id: string;
}

// Difficulty Configuration
export interface DifficultyConfig {
  difficulty: QuestionDifficulty;
  name: string;
  description: string;
  pointsMultiplier: number;
  timeMultiplier: number;
  color: string;
  icon: string;
  minPoints: number;
  maxPoints: number;
  defaultTimeLimit: number;
}

// Quiz Templates
export interface QuizTemplate {
  id: string;
  name: string;
  description: string;
  categories: QuestionCategory[];
  difficulties: QuestionDifficulty[];
  questionCount: number;
  timeLimit: number; // total quiz time in minutes
  passingScore: number; // percentage
  randomizeQuestions: boolean;
  randomizeOptions: boolean;
  showCorrectAnswers: boolean;
  allowRetakes: boolean;
  maxAttempts: number;
  coinReward: number;
  bonusMultiplier: number;
  enabled: boolean;
  createdAt: string;
  updatedAt: string;
}

// Analytics Types
export interface QuizAnalytics {
  totalQuestions: number;
  activeQuestions: number;
  questionsByCategory: {
    [key in QuestionCategory]: number;
  };
  questionsByDifficulty: {
    [key in QuestionDifficulty]: number;
  };
  questionsByStatus: {
    [key in QuestionStatus]: number;
  };
  averageCorrectAnswerRate: number;
  averageResponseTime: number;
  mostPopularCategories: CategoryPopularity[];
  leastPerformingQuestions: Question[];
  topPerformingQuestions: Question[];
  recentActivity: QuizActivity[];
}

export interface CategoryPopularity {
  category: QuestionCategory;
  categoryName: string;
  questionCount: number;
  totalPlays: number;
  averageScore: number;
  popularityRank: number;
}

export interface QuizActivity {
  id: string;
  type: 'question_added' | 'question_updated' | 'question_deleted' | 'category_updated' | 'quiz_played';
  description: string;
  questionId?: string;
  userId?: string;
  username?: string;
  timestamp: string;
  metadata?: {
    category?: QuestionCategory;
    difficulty?: QuestionDifficulty;
    score?: number;
    timeSpent?: number;
  };
}

// API Request/Response Types
export interface CreateQuestionRequest {
  question: string;
  type: QuestionType;
  category: QuestionCategory;
  difficulty: QuestionDifficulty;
  options: Omit<QuestionOption, 'id'>[];
  correctAnswer?: string;
  explanation?: string;
  points: number;
  timeLimit: number;
  tags: string[];
  language: string;
  imageUrl?: string;
  audioUrl?: string;
  videoUrl?: string;
}

export interface UpdateQuestionRequest extends Partial<CreateQuestionRequest> {
  id: string;
  status?: QuestionStatus;
}

export interface DeleteQuestionRequest {
  id: string;
  reason?: string;
}

export interface BulkUpdateQuestionsRequest {
  questionIds: string[];
  updates: {
    status?: QuestionStatus;
    category?: QuestionCategory;
    difficulty?: QuestionDifficulty;
    tags?: string[];
  };
}

export interface CreateQuizTemplateRequest {
  name: string;
  description: string;
  categories: QuestionCategory[];
  difficulties: QuestionDifficulty[];
  questionCount: number;
  timeLimit: number;
  passingScore: number;
  randomizeQuestions: boolean;
  randomizeOptions: boolean;
  showCorrectAnswers: boolean;
  allowRetakes: boolean;
  maxAttempts: number;
  coinReward: number;
  bonusMultiplier: number;
  enabled: boolean;
}

export interface UpdateQuizTemplateRequest extends Partial<CreateQuizTemplateRequest> {
  id: string;
}

// List Response Types
export interface QuestionList {
  questions: Question[];
  totalCount: number;
  nextCursor?: string;
}

export interface QuizTemplateList {
  templates: QuizTemplate[];
  totalCount: number;
}

// Search and Filter Types
export interface QuestionFilters {
  category?: QuestionCategory;
  difficulty?: QuestionDifficulty;
  status?: QuestionStatus;
  type?: QuestionType;
  tags?: string[];
  language?: string;
  createdBy?: string;
  dateRange?: {
    startDate: string;
    endDate: string;
  };
  usageRange?: {
    min: number;
    max: number;
  };
  correctAnswerRateRange?: {
    min: number;
    max: number;
  };
  searchText?: string;
}

export interface QuizTemplateFilters {
  enabled?: boolean;
  categories?: QuestionCategory[];
  difficulties?: QuestionDifficulty[];
  questionCountRange?: {
    min: number;
    max: number;
  };
  searchText?: string;
}

// Dashboard Types
export interface QuizManagementDashboard {
  questions: Question[];
  categories: QuestionCategoryInfo[];
  difficulties: DifficultyConfig[];
  templates: QuizTemplate[];
  analytics: QuizAnalytics;
  summary: {
    totalQuestions: number;
    activeQuestions: number;
    totalCategories: number;
    totalTemplates: number;
    activeTemplates: number;
    averageCorrectAnswerRate: number;
    questionsAddedToday: number;
    questionsPlayedToday: number;
  };
}

// Import/Export Types
export interface QuestionImportData {
  questions: Omit<Question, 'id' | 'createdAt' | 'updatedAt' | 'usageCount' | 'correctAnswerRate' | 'averageResponseTime'>[];
  overwriteExisting: boolean;
  validateBeforeImport: boolean;
}

export interface QuestionExportOptions {
  format: 'json' | 'csv' | 'excel';
  includeAnalytics: boolean;
  filters?: QuestionFilters;
  fields: string[];
}

export interface QuestionValidationResult {
  isValid: boolean;
  errors: string[];
  warnings: string[];
  question: Partial<Question>;
}

// Bulk Operations
export interface BulkOperationResult {
  success: boolean;
  processedCount: number;
  successCount: number;
  failureCount: number;
  errors: {
    questionId: string;
    error: string;
  }[];
}

// Question Statistics
export interface QuestionStats {
  questionId: string;
  totalPlays: number;
  correctAnswers: number;
  incorrectAnswers: number;
  correctAnswerRate: number;
  averageResponseTime: number;
  skipCount: number;
  reportCount: number;
  lastPlayed: string;
  popularityScore: number;
  difficultyRating: number; // user-perceived difficulty
}

// Lifeline System
export enum LifelineType {
  FIFTY_FIFTY = '50_50',
  SKIP_QUESTION = 'skip',
  EXTRA_TIME = 'extra_time',
  HINT = 'hint',
  AUDIENCE_POLL = 'audience_poll'
}

export interface LifelineConfig {
  type: LifelineType;
  name: string;
  description: string;
  coinCost: number;
  enabled: boolean;
  usageLimit: number; // per quiz session
  icon: string;
  color: string;
}

// Backend enum mappings (C# compatible)
// Difficulty levels (age-based or admin-set)
export enum BackendDifficulty {
  Easy = 0,    // For younger users or basic questions
  Medium = 1,  // Standard
  Hard = 2     // Advanced
}

// Lifeline types (player selects 3 out of 4 per level)
export enum BackendLifelineType {
  FiftyFifty = 0,   // Removes 2 wrong answers (-5 coins default)
  PassQuestion = 1, // Skip without penalty (-8 coins default)
  AutoCorrect = 2,  // Auto-selects correct (-15 coins default)
  Hint = 3          // Shows clue (text/image, -10 coins default)
}

// Serializer / deserializer helpers between frontend models and backend enums

// Difficulty <-> BackendDifficulty
export const serializeDifficulty = (difficulty: QuestionDifficulty): BackendDifficulty => {
  switch (difficulty) {
    case QuestionDifficulty.EASY:
      return BackendDifficulty.Easy;
    case QuestionDifficulty.MEDIUM:
      return BackendDifficulty.Medium;
    case QuestionDifficulty.HARD:
      return BackendDifficulty.Hard;
    default:
      return BackendDifficulty.Medium;
  }
};

export const deserializeDifficulty = (value: number): QuestionDifficulty => {
  switch (value) {
    case BackendDifficulty.Easy:
      return QuestionDifficulty.EASY;
    case BackendDifficulty.Medium:
      return QuestionDifficulty.MEDIUM;
    case BackendDifficulty.Hard:
      return QuestionDifficulty.HARD;
    default:
      return QuestionDifficulty.MEDIUM;
  }
};

// LifelineType <-> BackendLifelineType
// Note: Only the overlapping lifelines are mapped; EXTRA_TIME and AUDIENCE_POLL
// are frontend-only and don't have direct backend enum values in this scheme.
export const serializeLifelineType = (lifeline: LifelineType): BackendLifelineType | null => {
  switch (lifeline) {
    case LifelineType.FIFTY_FIFTY:
      return BackendLifelineType.FiftyFifty;
    case LifelineType.SKIP_QUESTION:
      return BackendLifelineType.PassQuestion;
    case LifelineType.HINT:
      return BackendLifelineType.Hint;
    default:
      // EXTRA_TIME, AUDIENCE_POLL or any unknown types
      return null;
  }
};

export const deserializeLifelineType = (value: number): LifelineType => {
  switch (value) {
    case BackendLifelineType.FiftyFifty:
      return LifelineType.FIFTY_FIFTY;
    case BackendLifelineType.PassQuestion:
      return LifelineType.SKIP_QUESTION;
    case BackendLifelineType.AutoCorrect:
      // Map AutoCorrect to HINT on the frontend by default
      return LifelineType.HINT;
    case BackendLifelineType.Hint:
      return LifelineType.HINT;
    default:
      return LifelineType.HINT;
  }
};

// Question Reporting
export interface QuestionReport {
  id: string;
  questionId: string;
  reportedBy: string;
  reporterUsername: string;
  reason: string;
  description: string;
  status: 'pending' | 'reviewed' | 'resolved' | 'dismissed';
  reviewedBy?: string;
  reviewerUsername?: string;
  reviewNotes?: string;
  createdAt: string;
  reviewedAt?: string;
}

export interface CreateQuestionReportRequest {
  questionId: string;
  reason: string;
  description: string;
}

export interface ReviewQuestionReportRequest {
  reportId: string;
  status: 'resolved' | 'dismissed';
  reviewNotes: string;
  questionAction?: 'none' | 'update' | 'disable' | 'delete';
}

// Backend category response types
export interface BackendQuizCategory {
  categoryId: string;
  categoryName: string;
  difficulty: number; // 0=Easy, 1=Medium, 2=Hard
  isActive: boolean;
}

export interface BackendQuizCategoriesResponse {
  categories: Record<string, BackendQuizCategory>;
}

export interface AddQuizCategoryRequest {
  categoryId: string;
  categoryName: string;
  difficulty: number; // 0=Easy, 1=Medium, 2=Hard
  isActive: boolean;
}
